<template>
  <section
    data-cy="property-contacts-view"
    :data-view-ready="!loading">
    <coozzy-card class="global-card no-top-border">
      <div class="row mb-2">
        <div class="col pl-0">
          <h5 class="ml-3 pt-1">
            {{ contactsCount }} {{ $t('message.marketingObjects.details.tabs.contacts') }}
          </h5>
        </div>
      </div>
      <div class="row mb-2">
        <div class="col-12 col-md-2">
          <coozzy-form-input
            v-model="query"
            data-cy="input-contacts-search"
            :placeholder="$t('message.generic.search')"
            type="text" />
        </div>
        <div class="col-12 col-md-2">
          <coozzy-multiselect
            v-model="rolesInBuildingFilter"
            data-cy="select-contacts-role"
            :options="computedRoleInBuilding"
            :multiple="true"
            :custom-label="customRolesInBuildingLabel"
            :placeholder="$t('message.onBoarding.contactsTab.roleInProperty').toString()" />
        </div>
        <div class="col-12 col-md-2">
          <coozzy-multiselect
            v-model="industriesFilter"
            data-cy="select-contacts-industry"
            :options="translatedIndustries"
            :multiple="true"
            :placeholder="$t('message.generic.industries').toString()"
            :custom-label="customLabel"
            track-by="label" />
        </div>
        <div
          v-if="isFiltered"
          class="col-12 col-md-2">
          <coozzy-button
            class="w-100 sort"
            data-cy="btn-contacts-clear-filters"
            @click="initFilter()">
            <coozzy-filter-icon /> {{ $t('message.generic.clearFilters') }}
          </coozzy-button>
        </div>
        <div
          v-if="isEditor"
          class="col-12 action-object"
          :class="isFiltered ? 'col-md-4' : 'col-md-6'">
          <coozzy-dropdown
            size="sm"
            class="float-right sort"
            data-cy="btn-contacts-add"
            :text="$t('message.generic.addContacts')">
            <coozzy-dropdown-item
              v-if="property.type === 'PROPERTY_TYPE_CONDOMINIUM_OWNERSHIP'"
              :disabled="disableButtons"
              @click="callAction('addDelegate');$event.stopPropagation()">
              {{ $t('message.manageBuilding.bulkAction.addDelegate') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-item
              v-if="property.type === 'PROPERTY_TYPE_CONDOMINIUM_OWNERSHIP'"
              :disabled="disableButtons"
              @click="callAction('addRevisor');$event.stopPropagation()">
              {{ $t('message.manageBuilding.bulkAction.addRevisor') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-item
              data-cy="property-contacts-add-service-provider-item"
              :disabled="disableButtons"
              @click="callAction('addServiceProviders');$event.stopPropagation()">
              {{ $t('message.manageBuilding.bulkAction.addServiceProviders') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-item
              data-cy="property-contacts-add-craftsman-item"
              :disabled="disableButtons"
              @click="callAction('addCraftsmen');$event.stopPropagation()">
              {{ $t('message.manageBuilding.bulkAction.addCraftsmen') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-item
              data-cy="property-contacts-add-janitor-item"
              :disabled="disableButtons"
              @click="callAction('addJanitors');$event.stopPropagation()">
              {{ $t('message.manageBuilding.bulkAction.addJanitors') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-item
              data-cy="property-contacts-add-tenant-change-item"
              :disabled="disableButtons"
              @click="callAction('addTenantChange');$event.stopPropagation()">
              {{ $t('message.manageBuilding.bulkAction.addTenantChange') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-item
              data-cy="property-contacts-add-owner-item"
              :disabled="disableButtons"
              @click="callAction('addOwner');$event.stopPropagation()">
              {{ $t('message.manageBuilding.bulkAction.addOwner') }}
            </coozzy-dropdown-item>
          </coozzy-dropdown>
        </div>
      </div>
      <div class="row mb-2 align-items-center">
        <div class="col-12 col-md-2">
          <coozzy-dropdown
            design="green"
            size="sm"
            class="w-100"
            data-cy="btn-contacts-selected-actions"
            :text="selectedEntriesText">
            <coozzy-dropdown-item
              v-if="isEditor"
              data-cy="property-contacts-selected-create-activity-item"
              :disabled="selectedEntries.length === 0"
              @click="showActivity()">
              {{ $t('message.contact.activity.create') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-item
              data-cy="property-contacts-selected-create-reminder-item"
              :disabled="selectedEntries.length === 0"
              @click="$refs['reminder-creation'].show()">
              {{ $t('message.calendar.reminder.create') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-item
              data-cy="property-contacts-selected-edit-note-item"
              :disabled="!editNoteEnabled"
              @click="openEditNote()">
              {{ $t('message.onBoarding.buildings.editNote') }}
            </coozzy-dropdown-item>
            <coozzy-dropdown-space-divider v-if="isEditor" />
            <coozzy-dropdown-item
              v-if="isEditor"
              data-cy="property-contacts-selected-remove-item"
              :disabled="selectedEntries.length === 0"
              @click="selectedEntries.length !== 0 ? $bvModal.show('removeFromBuildingConfirmationModal') : ''">
              {{ $t('message.onBoarding.contactsTab.confirmation.title') }}
            </coozzy-dropdown-item>
          </coozzy-dropdown>
        </div>
        <div class="col-md-2 col-sm-6 col-12">
          <template v-if="selectAllVisibleEntries && nbrTotalOfHits > nbrPerPage && nbrTotalOfHits <= 1000">
            <coozzy-form-checkbox
              v-model="selectAllEntries"
              data-cy="checkbox-contacts-select-all"
              :initial="selectAllEntries"
              :class="'p-0 w-100 btn-height-auto'"
              name="selectAllEntries"
              button>
              {{ selectAllEntries ? $t('message.generic.unselectHiddenEntries', { amount: nbrTotalOfHits }) : $t('message.generic.selectHiddenEntries', { amount: nbrTotalOfHits }) }}
            </coozzy-form-checkbox>
          </template>
        </div>
        <div
          v-if="isCompanyAdmin === true"
          class="offset-md-5 col-md-1 d-flex justify-content-end align-items-center">
          <div
            data-cy="btn-contacts-columns"
            @click="openAssembleListModal()">
            <coozzy-filters-icon
              v-b-tooltip="{container: '#rowConfig', title: $t('message.generic.tooltipAssembleList')}" />
          </div>
        </div>
        <div
          :class="isCompanyAdmin !== true ? 'offset-md-6' : ''"
          class="col-md-2 col-sm-6 col-12 mt-2 mt-sm-0">
          <coozzy-form-select
            v-model="nbrPerPage"
            data-cy="select-contacts-page-size"
            name="numberEntries"
            class="select-entries float-right">
            <option value="10">
              10 {{ $t('message.generic.entries') }}
            </option>
            <option value="50">
              50 {{ $t('message.generic.entries') }}
            </option>
            <option value="100">
              100 {{ $t('message.generic.entries') }}
            </option>
            <option value="500">
              500 {{ $t('message.generic.entries') }}
            </option>
            <option value="1000">
              1000 {{ $t('message.generic.entries') }}
            </option>
          </coozzy-form-select>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <b-table
            :key="contactsTableRenderKey"
            ref="contactsTable"
            data-cy="table-contacts"
            hover
            bordered
            responsive="true"
            stacked="md"
            selectable
            select-mode="single"
            :busy="loading"
            :fields="fields"
            :items="paginatedContacts"
            class="overflow-auto shadow"
            @row-clicked="contactClicked">
            <!-- Busy state -->
            <template #table-busy>
              <div class="text-center text-danger my-2">
                <coozzy-spinner />
              </div>
            </template>

            <!-- Headings -->
            <template #head(checkbox)>
              <coozzy-form-checkbox
                ref="header_checkbox"
                data-cy="property-contacts-header-checkbox"
                @change="(value) => { handleCheckboxesInsideTable(value) }" />
            </template>
            <template #head(roleInBuilding)>
              {{ $t('message.onBoarding.contactsTab.roleInProperty') }}
            </template>
            <template #head(industries)>
              {{ $t('message.generic.industry') }}
            </template>
            <template #head(nameContact)>
              {{ $t('message.generic.name') }}
            </template>
            <template #head(address)>
              {{ $t('message.generic.address') }}
            </template>
            <template #head(communication)>
              {{ $t('message.generic.communication') }}
            </template>
            <template #head(contactPerson)>
              {{ $t('message.generic.contactPerson') }}
            </template>
            <template #head(notes)>
              {{ $t('message.generic.notes') }}
            </template>

            <!-- Body -->
            <template #cell(checkbox)="data">
              <coozzy-form-checkbox
                :id="'check_' + data.item.selectingID"
                data-cy="property-contacts-row-checkbox"
                @change="(value) => { contactSelected(data.item, value) }" />
            </template>
            <!-- Role in building -->
            <template #cell(roleInBuilding)="data">
              <span data-cy="property-contacts-role-value">
                {{ $t('message.onBoarding.createContactTab.' + data.item.roleInBuilding) }}
              </span>
            </template>
            <!-- Industries -->
            <template #cell(industries)="data">
              <!-- eslint-disable vue/no-v-html -->
              <span
                data-cy="property-contacts-industry-value"
                v-html="getTranslatedIndustries(data.item, true)" />
              <!-- eslint-enable vue/no-v-html -->
            </template>
            <!-- Name -->
            <template #cell(nameContact)="data">
              <router-link
                class="link"
                data-cy="property-contacts-name-link"
                :to="getContactDetailRoute(data.item.id ? data.item.id : data.item.objectID)">
                <span>
                  {{ data.item.name }}
                </span>
              </router-link>
            </template>
            <!-- Address -->
            <template #cell(address)="data">
              <address-text
                data-cy="property-contacts-address-value"
                :address="data.item.address" />
            </template>
            <!-- Communication -->
            <template #cell(communication)="data">
              <div
                v-if="getPhoneNumbers(data.item.phoneNumbers, 'MOBILE') !== '-'"
                class="row">
                <div class="col-1">
                  <coozzy-mobile-alt-icon />
                </div>
                <div class="col">
                  <a
                    class="link"
                    data-cy="property-contacts-phone-mobile-link"
                    :href="'tel:' + getPhoneNumbers(data.item.phoneNumbers, 'MOBILE')">
                    {{ getPhoneNumbers(data.item.phoneNumbers, 'MOBILE') }}
                  </a>
                </div>
              </div>
              <div
                v-if="getPhoneNumbers(data.item.phoneNumbers, 'HOME') !== '-'"
                class="row">
                <div class="col-1">
                  <coozzy-phone-icon />
                </div>
                <div class="col">
                  <a
                    class="link"
                    data-cy="property-contacts-phone-home-link"
                    :href="'tel:' + getPhoneNumbers(data.item.phoneNumbers, 'HOME')">
                    {{ getPhoneNumbers(data.item.phoneNumbers, 'HOME') }}
                  </a>
                </div>
              </div>
              <div
                v-if="getPhoneNumbers(data.item.phoneNumbers, 'WORK') !== '-'"
                class="row">
                <div class="col-1">
                  <coozzy-headset-icon />
                </div>
                <div class="col">
                  <a
                    class="link"
                    data-cy="property-contacts-phone-work-link"
                    :href="'tel:' + getPhoneNumbers(data.item.phoneNumbers, 'WORK')">
                    {{ getPhoneNumbers(data.item.phoneNumbers, 'WORK') }}
                  </a>
                </div>
              </div>
              <div
                v-if="getPhoneNumbers(data.item.phoneNumbers, 'FAX') !== '-'"
                class="row">
                <div class="col-1">
                  <coozzy-fax-icon />
                </div>
                <div class="col">
                  <a
                    class="link"
                    :href="'tel:' + getPhoneNumbers(data.item.phoneNumbers, 'FAX')">
                    {{ getPhoneNumbers(data.item.phoneNumbers, 'FAX') }}
                  </a>
                </div>
              </div>
              <div
                v-if="data.item.email !== ''"
                class="row">
                <div class="col-1">
                  <img
                    class="email-icon-img"
                    src="@/assets/icon/arroba.svg"
                    alt="Placeholder">
                </div>
                <div class="col">
                  <template v-if="data.item.email !== ''">
                    <a
                      class="link"
                      data-cy="property-contacts-email-link"
                      tabindex="0"
                      @click="sendEmailModal(data.item)">
                      {{ data.item.email }}
                    </a>
                  </template>
                  <template v-else>
                    {{ data.item.email | displayOptionalValue }}
                  </template>
                </div>
              </div>
              <div
                v-if="data.item.website !== ''"
                class="row">
                <div class="col-1">
                  <coozzy-globe-icon />
                </div>
                <div class="col">
                  <template v-if="data.item.website !== ''">
                    <a
                      class="link"
                      tabindex="0"
                      target="_blank"
                      :href="getUrlWebsite(data.item.website)">
                      {{ data.item.website }}
                    </a>
                  </template>
                  <template v-else>
                    {{ data.item.website | displayOptionalValue }}
                  </template>
                </div>
              </div>
            </template>
            <!-- Contact Person -->
            <template #cell(contactPerson)="data">
              <template v-if="filterActiveContactPersons(data.item).length">
                <coozzy-form-select
                  v-if="!readOnly"
                  v-model="data.item.selectedContactPerson"
                  :disabled="savingContactPersonRowKey === data.item.selectionKey || loading"
                  @click.stop
                  @input="(value) => onContactPersonSelected(data.item, value)"
                  @select="(value) => onContactPersonSelected(data.item, value)">
                  <option :value="null">
                    -
                  </option>
                  <option
                    v-for="person of filterActiveContactPersons(data.item)"
                    :key="person.id"
                    :value="person.id">
                    {{ person.name }}
                  </option>
                </coozzy-form-select>
                <template v-else>
                  <router-link
                    v-if="getSelectedContactPerson(data.item).name !== '-'"
                    class="link"
                    :to="getContactDetailRoute(getSelectedContactPerson(data.item).id)">
                    {{ getSelectedContactPerson(data.item).name }}
                  </router-link>
                  <span v-else>-</span>
                </template>
              </template>
              <span v-else>-</span>
            </template>
            <!-- Notes -->
            <template #cell(notes)="data">
              <p
                :id="`contact-note-${data.item.id}`"
                data-cy="property-contacts-note-value"
                class="text-msg">
                {{ getNote(data.item, buildingList) }}
              </p>
              <b-tooltip
                v-if="getNote(data.item, buildingList)"
                :target="`contact-note-${data.item.id}`"
                custom-class="tooltip-contact-note"
                triggers="hover">
                <p class="m-0">
                  {{ getNote(data.item, buildingList) }}
                </p>
              </b-tooltip>
            </template>
          </b-table>
          <b-pagination
            v-if="!loading"
            v-model="currentPage"
            data-cy="contacts-pagination"
            align="center"
            :total-rows="filteredContacts.length"
            :per-page="nbrPerPage" />
        </div>
      </div>

      <b-modal
        id="removeFromBuildingConfirmationModal"
        ref="removeFromBuildingConfirmationModal"
        data-cy="property-contacts-remove-confirmation-modal"
        no-close-on-backdrop
        :title="$t('message.onBoarding.contactsTab.confirmation.title')"
        hide-footer>
        <p>{{ $t('message.onBoarding.contactsTab.confirmation.text') }}</p>
        <div class="col">
          <coozzy-button
            size="small"
            class="border mb-0"
            data-cy="property-contacts-remove-cancel-button"
            design="transparent"
            @click="$bvModal.hide('removeFromBuildingConfirmationModal')">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
          <coozzy-button
            size="small"
            class="float-right mb-0"
            data-cy="property-contacts-remove-confirm-button"
            design="green"
            :disabled="loading"
            @click="deleteItem">
            {{ $t('message.generic.confirm') }}
          </coozzy-button>
        </div>
      </b-modal>
      <b-modal
        id="edit-note-modal"
        ref="edit-note-modal"
        data-cy="property-contacts-edit-note-modal"
        no-close-on-backdrop
        :title="$t('message.onBoarding.buildings.editNote')"
        hide-footer>
        <div class="col-12 mb-3">
          <coozzy-form-textarea
            v-model="note"
            data-cy="property-contacts-edit-note-textarea"
            :initial="note"
            :value-text-area="note"
            :text="note"
            :initial-value="note"
            :rows="3" />
        </div>
        <div class="col">
          <coozzy-button
            size="small"
            class="border mb-0"
            data-cy="property-contacts-edit-note-cancel-button"
            design="transparent"
            @click="$bvModal.hide('edit-note-modal')">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
          <coozzy-button
            size="small"
            class="float-right mb-0"
            data-cy="property-contacts-edit-note-save-button"
            design="green"
            :disabled="loading"
            @click="editNote">
            {{ $t('message.generic.form.save') }}
          </coozzy-button>
        </div>
      </b-modal>
      <object-contacts-bulk-action
        v-if="selectedActions"
        ref="objectContactsBulk"
        :title="selectedActions"
        :owner-ids="property?.ownerId ? [property.ownerId] : []"
        :building-ids="buildingList.filter(y => y.objectIds.length > 0).map(x => x.id)"
        :existing-contacts="buildingList
          .flatMap(building => building.contacts)
          .filter((contact, index, self) => contact.id && index === self.findIndex(c => c.id === contact.id && c.type === contact.type))"
        @action-done="contactAdded" />
      <reminder-creation-modal
        ref="reminder-creation"
        :references-ids="selectedEntriesIds"
        :source="'addressBook'"
        @new-reminder="resetSelectedEntries()" />
      <create-activity-modal
        ref="activity-creation"
        :references-ids="selectedEntriesIds"
        :ids="selectedEntriesIds"
        @activity-created="resetSelectedEntries()" />
    </coozzy-card>
    <assemble-list-modal
      ref="assembleListModal"
      :default-fields="dataFields"
      :fields="selectedFields"
      modal-id="assembleListModal"
      @save-fields="saveFields" />
  </section>
</template>

<script>
import Vue from 'vue'
import ObjectApi from '@/misc/apis/ObjectApi'
import ContactApi from '@/misc/apis/ContactApi'
import ObjectContactsBulkAction from '@/owner/ObjectContactsBulkAction'
import CoozzyButton from '@/framework/components/button/CoozzyButton'
import CoozzyFormCheckbox from '@/framework/components/form/checkbox/CoozzyFormCheckbox'
import { tables } from '@/mixins/tables'
import AddressText from '@/framework/components/misc/AddressText'
import CoozzyMobileAltIcon from '@/framework/components/icons/CoozzyMobileAltIcon'
import CoozzyPhoneIcon from '@/framework/components/icons/CoozzyPhoneIcon'
import CoozzyHeadsetIcon from '@/framework/components/icons/CoozzyHeadsetIcon'
import CoozzyGlobeIcon from '@/framework/components/icons/CoozzyGlobeIcon'
import { contact } from '@/mixins/contact'
import { routeChecks } from '@/mixins/routeChecks'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput'
import CoozzyMultiselect from '@/framework/components/multiselect/CoozzyMultiselect'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect'
import CoozzyDropdownItem from '@/framework/components/dropdown/CoozzyDropdownItem'
import CoozzyDropdownSpaceDivider from '@/framework/components/dropdown/CoozzyDropdownSpaceDivider'
import CoozzyDropdown from '@/framework/components/dropdown/CoozzyDropdown'
import ReminderCreationModal from '@/calendar/components/ReminderCreationModal'
import { mapMutations } from 'vuex'
import { algolia } from '@/mixins/algolia'
import CreateActivityModal from '@/contact/components/CreateActivityModal'
import CoozzyFaxIcon from '@/framework/components/icons/CoozzyFaxIcon'
import { user } from '@/mixins/user'
import CoozzyFormTextarea from '@/framework/components/form/textarea/CoozzyFormTextarea.vue'
import CoozzyCard from '@/framework/components/card/CoozzyCard.vue'
import CoozzyFilterIcon from '@/framework/components/icons/CoozzyFilterIcon.vue'
import CoozzyFiltersIcon from '@/framework/components/icons/CoozzyFiltersIcon.vue'
import AssembleListModal from '@/contact/components/AssembleListModal.vue'
import SearchApi from '@/misc/apis/SearchApi'

export default {
  name: 'ContactsTab',
  components: {
    AssembleListModal,
    CoozzyFiltersIcon,
    CoozzyFilterIcon,
    CoozzyCard,
    CoozzyFormTextarea,
    CoozzyFaxIcon,
    CreateActivityModal,
    ReminderCreationModal,
    CoozzyDropdown,
    CoozzyDropdownSpaceDivider,
    CoozzyDropdownItem,
    CoozzyFormSelect,
    CoozzyMultiselect,
    CoozzyFormInput,
    CoozzySpinner,
    CoozzyGlobeIcon,
    CoozzyHeadsetIcon,
    CoozzyPhoneIcon,
    CoozzyMobileAltIcon,
    AddressText,
    CoozzyFormCheckbox,
    CoozzyButton,
    ObjectContactsBulkAction
  },
  mixins: [routeChecks, tables, contact, algolia, user],
  props: {
    readOnly: {
      type: Boolean,
      default: false
    },
    property: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      note: '',
      loading: false,
      savingContactPersonRowKey: null,
      pendingContactPersonChanges: [],
      contactsTableRenderKey: 0,
      keyActivity: 0,
      selectedFields: [],
      dataFields: [
        {
          key: 'roleInBuilding',
          label: this.$t('message.onBoarding.contactsTab.roleInProperty'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true
        },
        {
          key: 'industries',
          label: this.$t('message.generic.industries'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true
        },
        {
          key: 'nameContact',
          label: this.$t('message.generic.name'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true
        },
        {
          key: 'address',
          label: this.$t('message.generic.address'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true
        },
        {
          key: 'communication',
          label: this.$t('message.generic.communication'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true
        },
        {
          key: 'contactPerson',
          label: this.$t('message.generic.contactPerson'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true
        },
        {
          key: 'notes',
          label: this.$t('message.generic.notes'),
          thClass: 'align-middle',
          tdClass: 'align-middle',
          selected: true
        }
      ],
      fields: [
        {
          key: 'checkbox',
          label: '',
          thClass: 'th-checkbox',
          tdClass: 'align-middle border-right-0 checkbox-cell'
        }
      ],
      selectedEntries: [],
      query: '',
      rolesInBuildingFilter: [],
      industriesFilter: [],
      availableRolesInBuilding: ['craftsmen', 'janitors', 'serviceProvider', 'delegate', 'revisor', 'tenantChange', 'owner'],
      availableIndustries: [],
      currentPage: 1,
      nbrPerPage: 10,
      selectedActions: '',
      type: null,
      idContact: null,
      subtype: null,
      buildingList: [],
      allContacts: [],
      craftsmanContactIds: [],
      janitorContactIds: [],
      serviceProviderContactIds: [],
      delegateContactIds: [],
      revisorContactIds: [],
      tenantChangeContactIds: [],
      ownerContactIds: [],
      selectAllVisibleEntries: false,
      selectAllEntries: false,
      allEntries: []
    }
  },
  computed: {
    computedRoleInBuilding() {
      return this.property.type === 'PROPERTY_TYPE_CONDOMINIUM_OWNERSHIP' ? this.availableRolesInBuilding : this.availableRolesInBuilding.filter(x => x !== 'delegate' && x !== 'revisor')
    },
    nbrTotalOfHits() {
      return this.filteredContacts.length
    },
    isFiltered() {
      return this.query !== '' || this.rolesInBuildingFilter.length > 0 || this.industriesFilter.length > 0
    },
    translatedIndustries() {
      const contacts = [...this.craftsmanContacts].concat([...this.janitorContacts]).concat([...this.serviceProviderContacts])
      const counts = []
      this.translatedIndustriesList(true).filter(x => {
        return contacts.flatMap(x => x.industries).includes(x.value)
      }).forEach(function(x) {
        const existingCount = counts.filter(e => e.value === x.value)
        if (existingCount.length === 0) {
          const sameNetwork = contacts.flatMap(x => x.industries).filter(z => z === x.value)
          if (existingCount.length === 0) {
            counts.push({
              count: sameNetwork.length,
              value: x.value,
              label: x.label
            })
          } else {
            counts.push({
              count: 1,
              value: x.value,
              label: x.label
            })
          }
        }
      })
      return counts
    },
    selectedEntriesText() {
      const count = this.selectAllEntries && this.selectAllVisibleEntries ? this.nbrTotalOfHits : this.selectedEntries.length
      return this.$t('message.generic.selectedEntries') + ':  ' + count
    },
    selectedEntriesIds() {
      return this.selectAllEntries ? this.allEntries.map(x => x.id || x.objectID) : this.selectedEntries.map(x => x.id || x.objectID)
    },
    editNoteEnabled() {
      if (this.selectAllEntries) {
        return false
      }
      return this.selectedEntries.length === 1
    },
    filteredContacts() {
      let contacts = []
      if (this.rolesInBuildingFilter.length === 0 || this.rolesInBuildingFilter.includes('craftsmen')) {
        contacts = contacts.concat([...this.craftsmanContacts].map(c => {
          const temp = Object.assign({}, c)
          temp.roleInBuilding = 'craftsmen'
          temp.selectingID = Date.now() + Math.random().toString(36).substr(2, 9)
          temp.selectionKey = this.getSelectionKey(temp)
          return temp
        }))
      }
      if (this.rolesInBuildingFilter.length === 0 || this.rolesInBuildingFilter.includes('janitors')) {
        contacts = contacts.concat([...this.janitorContacts].map(c => {
          const temp = Object.assign({}, c)
          temp.roleInBuilding = 'janitors'
          temp.selectingID = Date.now() + Math.random().toString(36).substr(2, 9)
          temp.selectionKey = this.getSelectionKey(temp)
          return temp
        }))
      }
      if (this.rolesInBuildingFilter.length === 0 || this.rolesInBuildingFilter.includes('serviceProvider')) {
        contacts = contacts.concat([...this.serviceProviderContacts].map(c => {
          const temp = Object.assign({}, c)
          temp.roleInBuilding = 'serviceProvider'
          temp.selectingID = Date.now() + Math.random().toString(36).substr(2, 9)
          temp.selectionKey = this.getSelectionKey(temp)
          return temp
        }))
      }
      if (this.rolesInBuildingFilter.length === 0 || this.rolesInBuildingFilter.includes('delegate')) {
        contacts = contacts.concat([...this.delegateContacts].map(c => {
          const temp = Object.assign({}, c)
          temp.roleInBuilding = 'delegate'
          temp.selectingID = Date.now() + Math.random().toString(36).substr(2, 9)
          temp.selectionKey = this.getSelectionKey(temp)
          return temp
        }))
      }
      if (this.rolesInBuildingFilter.length === 0 || this.rolesInBuildingFilter.includes('revisor')) {
        contacts = contacts.concat([...this.revisorContacts].map(c => {
          const temp = Object.assign({}, c)
          temp.roleInBuilding = 'revisor'
          temp.selectingID = Date.now() + Math.random().toString(36).substr(2, 9)
          temp.selectionKey = this.getSelectionKey(temp)
          return temp
        }))
      }
      if (this.rolesInBuildingFilter.length === 0 || this.rolesInBuildingFilter.includes('tenantChange')) {
        contacts = contacts.concat([...this.tenantChangeContacts].map(c => {
          const temp = Object.assign({}, c)
          temp.roleInBuilding = 'tenantChange'
          temp.selectingID = Date.now() + Math.random().toString(36).substr(2, 9)
          temp.selectionKey = this.getSelectionKey(temp)
          return temp
        }))
      }
      if (this.rolesInBuildingFilter.length === 0 || this.rolesInBuildingFilter.includes('owner')) {
        contacts = contacts.concat([...this.ownerContacts].map(c => {
          const temp = Object.assign({}, c)
          temp.roleInBuilding = 'owner'
          temp.selectingID = Date.now() + Math.random().toString(36).substr(2, 9)
          temp.selectionKey = this.getSelectionKey(temp)
          return temp
        }))
      }
      if (this.query) {
        const q = this.query.toLowerCase()
        contacts = contacts.filter(c => {
          const searchString = c.name + ' ' +
            c.birthName + ' ' +
            c.email + ' ' +
            c.phoneNumbers.map(phone => phone.number + ' ') +
            (c.address ? `${c.address.street} ${c.address.streetNumber} ${c.address.mailbox} ${c.address.subUnit} ${c.address.careOf} ${c.address.zip} ${c.address.city}` : ' ') +
            (c.contactPersons ? c.contactPersons.map(p => this.getContactPersonSearchString(p)) : ' ') +
            c.website + ' ' +
            this.getNotes(c).map(note => note + ' ') + ' ' +
            this.getTranslatedIndustries(c)

          return searchString.toLowerCase().includes(q)
        })
      }

      if (this.industriesFilter.length > 0) {
        const industries = this.industriesFilter.map(item => item.value)
        contacts = contacts.filter(c => {
          for (const industry of industries) {
            if (c.industries.includes(industry)) {
              return true
            }
          }
          return false
        })
      }

      return contacts.map(c => this.enrichCompanyRowForContactPersonColumn(c, this.buildingList))
    },
    paginatedContacts() {
      return this.filteredContacts.slice((this.currentPage - 1) * this.nbrPerPage, this.currentPage * this.nbrPerPage)
    },
    contactsCount() {
      return this.filteredContacts !== null ? this.filteredContacts.length : 0
    },
    craftsmanContacts() {
      return [...this.removeDuplicatesContact(this.allContacts.filter(item =>
        this.contactAppearsInRoleList(item, this.craftsmanContactIds)))]
    },
    janitorContacts() {
      return [...this.removeDuplicatesContact(this.allContacts.filter(item =>
        this.contactAppearsInRoleList(item, this.janitorContactIds)))]
    },
    serviceProviderContacts() {
      return [...this.removeDuplicatesContact(this.allContacts.filter(item =>
        this.contactAppearsInRoleList(item, this.serviceProviderContactIds)))]
    },
    delegateContacts() {
      return [...this.removeDuplicatesContact(this.allContacts.filter(item =>
        this.contactAppearsInRoleList(item, this.delegateContactIds)))]
    },
    revisorContacts() {
      return [...this.removeDuplicatesContact(this.allContacts.filter(item =>
        this.contactAppearsInRoleList(item, this.revisorContactIds)))]
    },
    ownerContacts() {
      return [...this.removeDuplicatesContact(this.allContacts.filter(item =>
        this.contactAppearsInRoleList(item, this.ownerContactIds)))]
    },
    tenantChangeContacts() {
      return [...this.removeDuplicatesContact(this.allContacts.filter(item =>
        this.contactAppearsInRoleList(item, this.tenantChangeContactIds)))]
    },
    disableButtons() {
      return !this.buildingList.filter(x => x.objectIds.length > 0).length > 0
    },
    companySelectedContactPersonMap() {
      const map = new Map()
      this.buildingList.forEach(building => {
        building.contacts.forEach(contact => {
          this.allContacts.forEach(company => {
            if (company.contactPersons?.some(person => person.id === contact.id)) {
              map.set(company.id || company.objectID, {
                name: company.contactPersons.find(person => person.id === contact.id).name,
                id: contact.id
              })
            }
          })
        })
      })
      return map
    }
  },
  watch: {
    paginatedContacts() {
      this.handleCheckboxesInsideTable(false)
    },
    nbrPerPage: function (newVal) {
      localStorage.setItem('contactsTab-nbrPerPage', newVal)
    },
    selectAllEntries: async function (newValue) {
      if (newValue) {
        this.handleCheckboxesInsideTable(newValue)
        this.allEntries = this.filteredContacts
      }
    }
  },
  mounted() {
    this.loadInstantSearchInternal()
    this.nbrPerPage = localStorage.getItem('contactsTab-nbrPerPage') ? parseInt(localStorage.getItem('contactsTab-nbrPerPage')) : 10
    this.loading = true
    this.loadData().finally(() => {
      this.loading = false
    })
    this.dataFields = this.dataFields.filter(x => x.selected).concat(this.dataFields.filter(x => !x.selected))
    SearchApi.loadSettings(this.accountId).then(response => {
      const settings = response.tableSettings
      let hasOldList = false
      if (settings.length > 0) {
        settings.forEach(setting => {
          if (setting.columns.length > 0 && setting.table === 'componentsContactProperty') {
            hasOldList = true
            this.loadFields(setting)
          }
        })
      }
      if (hasOldList === false) {
        this.fields.push(...this.dataFields.filter(x => x.selected))
        this.selectedFields = JSON.parse(JSON.stringify(this.dataFields))
      }
    }).catch(e => {
      console.log(e)
    })
  },
  methods: {
    ...mapMutations('message', [
      'setSendMailContacts',
      'setSendMailPreselectedContact',
      'setSendMailReferenceIds',
      'setSendMailEmailToReply'
    ]),
    loadFields(setting) {
      const result = this.getFields(this.dataFields, setting)
      this.fields = result?.fields
      this.selectedFields = result?.selectedFields
      this.$refs.contactsTable?.refresh()
    },
    saveFields(data) {
      const columns = data.filter(x => x.selected === true).map(y => y.key)
      SearchApi.saveSettings(this.accountId, columns, 'componentsContactProperty').then(response => {
        const setting = response.tableSettings
        if (setting && setting.columns.length > 0) {
          this.loadFields(setting)
        }
        this.$bvModal.hide('assembleListModal')
      })
    },
    loadData() {
      if (!this.property || !this.property.buildingIds || this.property.buildingIds.length === 0) {
        this.buildingList = []
        this.craftsmanContactIds = []
        this.janitorContactIds = []
        this.serviceProviderContactIds = []
        this.delegateContactIds = []
        this.revisorContactIds = []
        this.tenantChangeContactIds = []
        this.ownerContactIds = []
        this.allContacts = []
        return Promise.resolve()
      }
      return ObjectApi.getBuildingsListByIds(this.property.buildingIds)
        .then(response => {
          this.buildingList = response.buildings
          this.craftsmanContactIds = []
          this.janitorContactIds = []
          this.serviceProviderContactIds = []
          this.delegateContactIds = []
          this.revisorContactIds = []
          this.tenantChangeContactIds = []
          this.ownerContactIds = []

          this.buildingList.forEach(element => {
            if (element.contacts.length > 0) {
              element.contacts.forEach(contact => {
                switch (contact.type) {
                  case 'BUILDING_CONTACT_TYPE_CRAFTSMAN': {
                    this.craftsmanContactIds.push(contact.id)
                    break
                  }
                  case 'BUILDING_CONTACT_TYPE_JANITOR': {
                    this.janitorContactIds.push(contact.id)
                    break
                  }
                  case 'BUILDING_CONTACT_TYPE_SERVICE_PROVIDER': {
                    this.serviceProviderContactIds.push(contact.id)
                    break
                  }
                  case 'BUILDING_CONTACT_TYPE_DELEGATE': {
                    this.delegateContactIds.push(contact.id)
                    break
                  }
                  case 'BUILDING_CONTACT_TYPE_REVISOR': {
                    this.revisorContactIds.push(contact.id)
                    break
                  }
                  case 'BUILDING_CONTACT_TYPE_TENANT_CHANGE': {
                    this.tenantChangeContactIds.push(contact.id)
                    break
                  }
                  case 'BUILDING_CONTACT_TYPE_OWNER': {
                    this.ownerContactIds.push(contact.id)
                    break
                  }
                }
              })
            }
          })
          const filteredContactIds = this.collectBuildingContactIds(this.buildingList)
          return this.resolveContactsByIds(filteredContactIds)
            .then(resolved => {
              this.allContacts = resolved
              this.contactsTableRenderKey += 1
            })
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.building'), { type: 'error' })
        })
    },
    customLabel(item) {
      return item.label + ' (' + item.count + ')'
    },
    async contactAdded() {
      this.loading = true
      await this.loadData()
      this.loading = false
    },
    removeDuplicatesContact(array) {
      return array.reverse().reduce((acc, current) => {
        const currentObjectId = current.objectID ? current.objectID : current.id
        const x = acc.find(item => (item.objectID && item.objectID === currentObjectId) || (item.id && item.id === currentObjectId))
        if (!x) {
          return acc.concat([current])
        } else {
          return acc
        }
      }, [])
    },
    initFilter() {
      this.query = ''
      this.industriesFilter = []
      this.rolesInBuildingFilter = []
    },
    openAssembleListModal() {
      this.$bvModal.show('assembleListModal')
    },
    showActivity() {
      this.keyActivity = Math.random().toString(36).substr(2, 9)
      this.$nextTick(() => {
        this.$refs['activity-creation'].show()
      })
    },
    deleteItem() {
      this.loading = true
      const buildingContacts = []
      const buildingIds = []
      const entries = this.selectAllEntries ? this.allEntries : this.selectedEntries
      for (const contact of entries) {
        this.buildingList.forEach(building => {
          const buildingContactIds = buildingContacts.map(x => x.id)
          const contactRole = this.translatedRolesInBuilding[contact.roleInBuilding]
          // building contacts can be either a company or a contact person. selectedEntry is a regular contact or a company contact
          const existingContact = building.contacts.find(buildingContact => {
            if (buildingContact.id === contact.id && buildingContact.type === contactRole) {
              return true
            }

            if (contact?.contactPersons?.length > 0 && buildingContact.type === contactRole) {
              return contact.contactPersons.some(person => person.id === buildingContact.id)
            }

            return false
          })

          if (existingContact && !buildingContactIds.includes(existingContact.id)) {
            buildingContacts.push(existingContact)
          }
          if (existingContact && !buildingIds.includes(building.id)) {
            buildingIds.push(building.id)
          }
        })
      }
      ObjectApi.removeBuildingContacts(buildingContacts, buildingIds)
        .then(async () => {
          this.resetSelectedEntries()
          await this.loadData()
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.removeContact').toString(), { type: 'error' })
        })
        .finally(() => {
          this.$bvModal.hide('removeFromBuildingConfirmationModal')
          Vue.toasted.show(this.$t('message.successMessages.contactDeletedSuccess').toString(), { type: 'success' })
          this.loading = false
        })
    },
    resetSelectedEntries() {
      this.selectedEntries = []
      this.handleCheckboxesInsideTable(false)
    },
    callAction(action) {
      this.selectedActions = action
      this.$nextTick(function () {
        this.$refs.objectContactsBulk.show()
      })
    },
    sendEmailModal(contact) {
      this.setSendMailContacts([contact])
      this.setSendMailPreselectedContact(contact)
      this.setSendMailReferenceIds([])
      this.setSendMailEmailToReply(null)
      this.$nextTick(function () {
        this.$bvModal.show('modal-send-mail')
      })
    },
    getContactPersonSearchString(person) {
      return person.name + ' ' +
        person.birthName + ' ' +
        person.email + ' ' +
        person.phoneNumbers.map(phone => phone.number + ' ')
    },
    getSelectionKey(contact) {
      const contactId = contact.id || contact.objectID || contact.selectingID || 'unknown'
      return `${contactId}:${contact.roleInBuilding || 'unknown'}`
    },
    customRolesInBuildingLabel(roleInBuilding) {
      return this.$t(`message.onBoarding.createContactTab.${roleInBuilding}`)
    },
    handleCheckboxesInsideTable(value) {
      if (this.$refs.contactsTable) {
        const allCheckboxesComponents = this.$refs.contactsTable.$children[1].$children
        this.handleCheckbox(allCheckboxesComponents, value)
        this.selectAllVisibleEntries = value
        if (value === false) {
          this.selectAllEntries = false
        }
      }
      if (this.$refs.header_checkbox && value === false) {
        this.$refs.header_checkbox.checked = false
      }
    },
    contactSelected(contact, checked) {
      const selectionKey = contact.selectionKey || this.getSelectionKey(contact)
      if (checked) {
        const hasSelection = this.selectedEntries.some(entry =>
          (entry.selectionKey || this.getSelectionKey(entry)) === selectionKey
        )
        if (!hasSelection) {
          this.selectedEntries.push(contact)
        }
        if (this.selectedEntries.length === this.nbrPerPage) {
          this.selectAllVisibleEntries = true
        }
      } else {
        this.selectedEntries = this.selectedEntries.filter(entry =>
          (entry.selectionKey || this.getSelectionKey(entry)) !== selectionKey
        )
        this.selectAllEntries = false
        this.selectAllVisibleEntries = false
      }
    },
    contactClicked(item) {
      const contactId = item.objectID ? item.objectID : item.id
      this.redirectToDetailPage(contactId)
    },
    getUrlWebsite(item) {
      if (item.indexOf('//') > -1) {
        return item
      } else {
        return '//' + item
      }
    },
    openEditNote() {
      if (!this.editNoteEnabled) {
        return
      }
      if (this.selectedEntries.length === 1) {
        const selected = this.selectedEntries[0]
        const contactRole = this.translatedRolesInBuilding[selected.roleInBuilding]
        const contactId = selected.id || selected.objectID
        let resolvedNote = selected.note

        if (contactRole) {
          for (const building of this.buildingList) {
            const buildingContact = building.contacts.find(buildingContact => {
              if (buildingContact.type !== contactRole) {
                return false
              }
              if (buildingContact.id === contactId) {
                return true
              }
              if (selected?.contactPersons?.length > 0) {
                return selected.contactPersons.some(person => person.id === buildingContact.id)
              }
              return false
            })

            if (buildingContact) {
              resolvedNote = buildingContact.note ?? resolvedNote
              break
            }
          }
        }

        this.note = resolvedNote
      }
      this.$nextTick(() => {
        this.$refs['edit-note-modal'].show()
      })
    },
    editNote() {
      if (!this.editNoteEnabled) {
        return
      }
      const buildingContacts = []
      const buildingContactIds = new Set()
      const buildingIds = []
      const buildingIdSet = new Set()
      for (const contact of this.selectedEntries) {
        const contactRole = this.translatedRolesInBuilding[contact.roleInBuilding] || 'BUILDING_CONTACT_TYPE_UNDEFINED'
        const contactId = contact.id || contact.objectID
        this.buildingList.forEach(building => {
          const existingContact = building.contacts.find(buildingContact => {
            if (buildingContact.type !== contactRole) {
              return false
            }
            if (buildingContact.id === contactId) {
              return true
            }
            if (contact?.contactPersons?.length > 0) {
              return contact.contactPersons.some(person => person.id === buildingContact.id)
            }
            return false
          })

          if (existingContact) {
            const contactKey = `${existingContact.id}:${contactRole}`
            if (!buildingContactIds.has(contactKey)) {
              buildingContacts.push({
                id: existingContact.id,
                note: this.note,
                type: contactRole
              })
              buildingContactIds.add(contactKey)
            }
            if (!buildingIdSet.has(building.id)) {
              buildingIds.push(building.id)
              buildingIdSet.add(building.id)
            }
          }
        })
      }
      ObjectApi.editContactNote(buildingContacts, buildingIds)
        .then(() => {
          this.resetSelectedEntries()
          this.$refs['edit-note-modal'].hide()
          this.loadData()
          Vue.toasted.show(this.$t('message.successMessages.note').toString(), { type: 'success' })
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.note').toString(), { type: 'error' })
        })
    },
    /**
     * Building may store a contact person's id (COOZ). Include the company row when one of its
     * contactPersons matches. Omit the standalone person row if a company already represents them.
     */
    contactAppearsInRoleList(item, roleIds) {
      if (!roleIds?.length) {
        return false
      }
      const id = item.id || item.objectID
      if (roleIds.includes(id)) {
        if (item.contactPersons?.length) {
          return true
        }
        const personListedUnderCompany = this.allContacts.some(c =>
          c !== item &&
          Array.isArray(c.contactPersons) &&
          c.contactPersons.some(p => p.id === id))
        return !personListedUnderCompany
      }
      return !!(item.contactPersons?.some(p => roleIds.includes(p.id)))
    },
    see(refine, value) {
      refine(value)
    },
    getNotes(item) {
      let notes = []
      for (const building of this.buildingList) {
        const linked = building.contacts.find(bc =>
          bc.id === item.id || item.contactPersons?.some(p => p.id === bc.id))
        const note = linked?.note
        if (note) {
          notes.push(note)
        }
      }
      return notes
    },
    availableTenants(search) {
      if (!search || search.length === 0) {
        this.allContacts = []
        return
      }
      ContactApi.contactResolve(search)
        .then(response => {
          this.allContacts = response.persons.concat(response.companies)
        })
        .catch(e => {
          console.log(e)
        })
    },
    getSelectedContactPerson(contact) {
      const contactId = contact.id || contact.objectID
      return this.companySelectedContactPersonMap.get(contactId) || { name: '-', id: null }
    },
    onContactPersonSelected(row, value) {
      if (this.readOnly) {
        return
      }
      row.selectedContactPerson = value === '' || value === undefined || value === null ? null : value
      this.changeLinkedContactPerson(row)
    },
    getContactsBuildingList() {
      return this.buildingList?.length ? this.buildingList : []
    },
    changeLinkedContactPerson(row) {
      if (!row?.contactPersons?.length || this.readOnly) {
        return
      }
      const buildingList = this.getContactsBuildingList()
      const targetLinkId = this.getTargetContactLinkId(row, row.selectedContactPerson)
      const currentLinkId = this.getCurrentBuildingContactLinkId(row, buildingList)
      const selectionKey = row.selectionKey || this.getSelectionKey(row)
      if (this.contactPersonIdsEqual(targetLinkId, currentLinkId)) {
        this.removePendingContactPersonChange(selectionKey)
        return
      }
      const { buildingIds } = this.findBuildingLinksForCompanyRow(row, buildingList)
      if (!buildingIds.length) {
        return
      }
      this.queuePendingContactPersonChange({
        selectionKey,
        row: this.cloneRowForContactPersonPending(row),
        previousLinkId: currentLinkId,
        previousPersonId: this.getLinkedPersonIdForCompanyRow(row, buildingList)
      })
      this.notifyContactPersonChanged()
    },
    cloneRowForContactPersonPending(row) {
      return {
        id: row.id,
        objectID: row.objectID,
        roleInBuilding: row.roleInBuilding,
        contactPersons: row.contactPersons,
        selectedContactPerson: row.selectedContactPerson ?? null
      }
    },
    queuePendingContactPersonChange(change) {
      const index = this.pendingContactPersonChanges.findIndex(c => c.selectionKey === change.selectionKey)
      if (index >= 0) {
        this.pendingContactPersonChanges.splice(index, 1, change)
      } else {
        this.pendingContactPersonChanges.push(change)
      }
    },
    removePendingContactPersonChange(selectionKey) {
      this.pendingContactPersonChanges = this.pendingContactPersonChanges.filter(c => c.selectionKey !== selectionKey)
      if (!this.pendingContactPersonChanges.length) {
        this.$emit('has-change', false)
      }
    },
    notifyContactPersonChanged() {
      this.$emit('has-change', true)
    },
    hasPendingContactPersonChanges() {
      return this.pendingContactPersonChanges.length > 0
    },
    async savePendingContactPersonChanges() {
      if (!this.pendingContactPersonChanges.length) {
        return
      }
      this.loading = true
      const changes = [...this.pendingContactPersonChanges]
      try {
        for (const pending of changes) {
          this.savingContactPersonRowKey = pending.selectionKey
          await this.persistContactPersonChange(pending.row, this.buildingList)
        }
        this.pendingContactPersonChanges = []
        await this.loadData()
        await this.$nextTick()
        this.$refs.contactsTable?.refresh()
        Vue.toasted.show(this.$t('message.successMessages.contactLinked').toString(), { type: 'success' })
      } catch (e) {
        console.log(e)
        for (const pending of changes) {
          const row = this.filteredContacts.find(c => c.selectionKey === pending.selectionKey)
          if (row) {
            row.selectedContactPerson = pending.previousPersonId ?? null
          }
        }
        this.pendingContactPersonChanges = changes
        Vue.toasted.show(this.$t('message.savingErrors.removeContact').toString(), { type: 'error' })
        throw e
      } finally {
        this.savingContactPersonRowKey = null
        this.loading = false
      }
    }
  }
}
</script>
<style lang="scss" scoped>
p.text-msg {
  line-height: 1.5em;
  height: 1.5em;
  max-width: 200px;
  overflow: hidden;
  line-break: anywhere;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
